import React, { useState, useEffect } from 'react';
import { useOrderModal } from '../contexts/OrderModalContext';

import Navigation from './Navigation';
import Footer from './Footer';
import LocationPageContent from './LocationPageContent';
import PageSEO from './PageSEO'; // Import the SEO component

// Updated LocationsPleasantHillView component
const LocationsPleasantHillView = ({ locationName = "Pleasant Hill" }) => {
  const [locationData, setLocationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const { openModal } = useOrderModal();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchLocationData = async () => {
      try {
        setLoading(true);
        // Using the API endpoint from your router
        const response = await fetch(`/api/locations/${locationName}`);
        const result = await response.json();
        
        if (result.success) {
          setLocationData(result.data);
        } else {
          setError(result.message || 'Failed to load location data');
        }
      } catch (err) {
        setError('Failed to fetch location data');
        console.error('Error fetching location:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLocationData();
  }, [locationName]);

  // Generate comprehensive SEO configuration for location page
  const pleasantHillSEO = locationData ? {
    title: `${locationData.name} | Kinder's Meats Deli BBQ & Catering`,
    description: `Visit Kinder's Meats Deli BBQ & Catering in ${locationData.address.city}, ${locationData.address.state}. Order pickup, delivery, or catering. Call ${locationData.phoneNumber}. Address: ${locationData.address.address1}, ${locationData.address.city}.`,
    keywords: [
      `Kinder's Meats ${locationData.address.city}`,
      `BBQ restaurant ${locationData.address.city}`,
      `${locationData.address.city} BBQ`,
      `${locationData.address.state} BBQ restaurant`,
      `Kinder's Meats ${locationData.address.state}`,
      'authentic BBQ',
      'deli sandwiches',
      'butcher counter',
      'cut-to-order steaks',
      'marinated chicken',
      `restaurant ${locationData.address.city}`,
      `catering ${locationData.address.city}`,
      `pickup ${locationData.address.city}`,
      `delivery ${locationData.address.city}`,
      'Ball Tip Sandwich',
      'smoked BBQ ribs',
      'BBQ catering',
      'family restaurant',
      'California BBQ',
      'East Bay BBQ',
      `${locationData.address.city} restaurant`,
      `${locationData.address.city} catering`,
      'backyard BBQ food',
      'tailgate food'
    ],
    canonicalUrl: `/locations/${locationName.toLowerCase().replace(/\s+/g, '-')}`,
    ogImage: locationData.photoSrc ? 
      `https://kindersmeats.com/images/locations/${locationData.photoSrc}` : 
      'https://kindersmeats.com/images/locations/kinders-pleasant-hill.jpg',
    customMeta: {
      'business:contact_data:street_address': `${locationData.address.address1} ${locationData.address.address2 || ''}`,
      'business:contact_data:locality': locationData.address.city,
      'business:contact_data:region': locationData.address.state,
      'business:contact_data:postal_code': locationData.address.zip,
      'business:contact_data:country_name': 'United States',
      'business:contact_data:phone_number': locationData.phoneNumber,
      'geo.placename': `${locationData.address.city}, ${locationData.address.state}`,
      'geo.region': locationData.address.state,
      ...(locationData.coordinates && {
        'ICBM': `${locationData.coordinates.lat}, ${locationData.coordinates.lng}`,
        'geo.position': `${locationData.coordinates.lat};${locationData.coordinates.lng}`
      })
    },
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": ["LocalBusiness", "Restaurant"],
          "@id": `https://kindersmeats.com/locations/${locationName.toLowerCase().replace(/\s+/g, '-')}#business`,
          "name": locationData.name,
          "alternateName": "Kinder's Meats",
          "description": `Kinder's Meats Deli BBQ & Catering location in ${locationData.address.city}, ${locationData.address.state}. Serving premium BBQ, deli sandwiches, and catering services.`,
          "url": `https://kindersmeats.com/locations/${locationName.toLowerCase().replace(/\s+/g, '-')}`,
          "telephone": locationData.phoneNumber,
          "priceRange": "$$",
          "servesCuisine": ["American", "BBQ", "Deli"],
          "paymentAccepted": ["Cash", "Credit Card"],
          "currenciesAccepted": "USD",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": `${locationData.address.address1}${locationData.address.address2 ? ' ' + locationData.address.address2 : ''}`,
            "addressLocality": locationData.address.city,
            "addressRegion": locationData.address.state,
            "postalCode": locationData.address.zip,
            "addressCountry": "US"
          },
          ...(locationData.coordinates && {
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": locationData.coordinates.lat,
              "longitude": locationData.coordinates.lng
            }
          }),
          ...(locationData.hours && {
            "openingHours": Object.entries(locationData.hours)
              .filter(([day, hours]) => hours && hours !== 'Closed')
              .map(([day, hours]) => {
                const dayMap = {
                  'monday': 'Mo', 'tuesday': 'Tu', 'wednesday': 'We',
                  'thursday': 'Th', 'friday': 'Fr', 'saturday': 'Sa', 'sunday': 'Su'
                };
                return `${dayMap[day.toLowerCase()]} ${hours}`;
              })
          }),
          "image": locationData.photoSrc ? 
            `https://kindersmeats.com/images/locations/${locationData.photoSrc}` : 
            'https://kindersmeats.com/images/locations/kinders-pleasant-hill.jpg',
          "parentOrganization": {
            "@type": "Organization",
            "name": "Kinder's Meats Deli BBQ & Catering",
            "url": "https://kindersmeats.com",
            "logo": "https://kindersmeats.com/images/kinders-logo.png"
          },
          "hasMenu": "https://kindersmeats.com/menu",
          "areaServed": {
            "@type": "City",
            "name": locationData.address.city
          },
          "makesOffer": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Dine-in"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Takeout"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Catering"
              }
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": locationData.phoneNumber,
            "contactType": "customer service"
          }
        },
        {
          "@type": "Organization",
          "@id": "https://kindersmeats.com/#organization",
          "name": "Kinder's Meats Deli BBQ & Catering",
          "url": "https://kindersmeats.com",
          "logo": "https://kindersmeats.com/images/kinders-logo.png",
          "sameAs": [
            "https://www.facebook.com/KindersMeats",
            "https://www.instagram.com/kindersmeats",
            "https://www.yelp.com/biz/kinders-meats"
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": locationData.phoneNumber,
            "contactType": "customer service"
          }
        },
        {
          "@type": "WebPage",
          "@id": `https://kindersmeats.com/locations/${locationName.toLowerCase().replace(/\s+/g, '-')}#webpage`,
          "url": `https://kindersmeats.com/locations/${locationName.toLowerCase().replace(/\s+/g, '-')}`,
          "name": `${locationData.name} | Kinder's Meats Deli BBQ & Catering`,
          "description": `Visit Kinder's Meats Deli BBQ & Catering in ${locationData.address.city}, ${locationData.address.state}. Order pickup, delivery, or catering. Call ${locationData.phoneNumber}. Address: ${locationData.address.address1}, ${locationData.address.city}.`,
          "isPartOf": {
            "@id": "https://kindersmeats.com/#website"
          },
          "about": {
            "@id": `https://kindersmeats.com/locations/${locationName.toLowerCase().replace(/\s+/g, '-')}#business`
          },
          "mainEntity": {
            "@id": `https://kindersmeats.com/locations/${locationName.toLowerCase().replace(/\s+/g, '-')}#business`
          },
          "breadcrumb": {
            "@id": `https://kindersmeats.com/locations/${locationName.toLowerCase().replace(/\s+/g, '-')}#breadcrumb`
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": `https://kindersmeats.com/locations/${locationName.toLowerCase().replace(/\s+/g, '-')}#breadcrumb`,
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://kindersmeats.com"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Locations",
              "item": "https://kindersmeats.com/locations"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": locationData.name,
              "item": `https://kindersmeats.com/locations/${locationName.toLowerCase().replace(/\s+/g, '-')}`
            }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": `What are the hours for Kinder's Meats in ${locationData.address.city}?`,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": `Our ${locationData.address.city} location hours vary by day. Please call ${locationData.phoneNumber} or visit our location page for current hours and any holiday schedules.`
              }
            },
            {
              "@type": "Question", 
              "name": `Do you offer delivery in ${locationData.address.city}?`,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": `Yes, we offer delivery and pickup options at our ${locationData.address.city} location. You can order online or call ${locationData.phoneNumber} to place your order.`
              }
            },
            {
              "@type": "Question",
              "name": `What makes Kinder's ${locationData.address.city} location special?`,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": `Our ${locationData.address.city} location features the same authentic BBQ and signature Ball Tip Sandwich that Kinder's has been known for since 1946, plus a full butcher counter with cut-to-order steaks and marinated meats.`
              }
            },
            {
              "@type": "Question",
              "name": `Do you cater events in ${locationData.address.city}?`,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": `Absolutely! We provide BBQ catering services throughout ${locationData.address.city} and surrounding areas. Contact our ${locationData.address.city} location at ${locationData.phoneNumber} to discuss your catering needs.`
              }
            }
          ]
        }
      ]
    }
  } : null;

  if (loading) {
    return (
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className="is-flex is-flex-direction-column fullHeight is-justify-content-center is-align-items-center">
          <p>Loading location details...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className="is-flex is-flex-direction-column fullHeight is-justify-content-center is-align-items-center">
          <p className="has-text-danger">Error: {error}</p>
        </div>
      </section>
    );
  }

  return (
    <>
      {/* SEO Component */}
      {pleasantHillSEO && <PageSEO {...pleasantHillSEO} />}
      
      {/* Main content with semantic HTML structure */}
      <main>
        <section className="theme-light m-0 p-0" data-theme="light">
          <div className='is-flex is-flex-direction-column fullHeight is-justify-content-space-between'>
            <div className='is-flex is-flex-direction-column is-justify-content-start'>
              <Navigation />

              <LocationPageContent 
                className="content-space-160 is-10-mobile is-10-tablet is-10-desktop is-8-widescreen is-8-fullhd is-offset-1-mobile is-offset-1-tablet is-offset-1-desktop is-offset-2-widescreen is-offset-2-fullhd"
                locationData={locationData}
              />
            </div>   
            
            <Footer />
          </div>
        </section>
      </main>
    </>
  );
};

export default LocationsPleasantHillView;